"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ImmutableState = void 0;
/* This class is used to implement the Immutable.js map interface.
 * We had to proxy through all the methods in this class so that the interface
 * would be implemented; Immutable.js doesn't supply a typescript class that
 * implements the interface that we can subclass
 */
var ImmutableState = /** @class */ (function () {
    function ImmutableState(state) {
        this.state = state;
        // Everything below this is implementing the ImmutableMap interface
        this._size = 0;
    }
    Object.defineProperty(ImmutableState.prototype, "nextEventOffset", {
        // nextEventOffset gets set by the engine base class on each state object
        // so it can know which point in the event stream it is for
        get: function () {
            return this.get('nextEventOffset');
        },
        enumerable: false,
        configurable: true
    });
    ImmutableState.prototype.newInstance = function (state) {
        var c = this.constructor;
        return new c(state);
    };
    // Let's add some custom functionality while we're here to make our lives easier
    ImmutableState.prototype.increment = function (key, by) {
        if (by === 0) {
            return this;
        }
        return this.newInstance(this.state.updateIn([key], function (oldValue) { return (oldValue || 0) + by; }));
    };
    ImmutableState.prototype.incrementIn = function (keyPath, by) {
        return this.newInstance(this.state.updateIn(keyPath, function (oldValue) { return (oldValue || 0) + by; }));
    };
    // Update an Immutable List inside this Map and set it to the specified size.
    // If expanding the array, any new indices created will be set to the defaultValue.
    ImmutableState.prototype.deepSetSize = function (keyPath, size, defaultValue) {
        if (defaultValue === void 0) { defaultValue = undefined; }
        return this.updateIn(keyPath, function (list) {
            var initialSize = list.size;
            if (initialSize === size) {
                return list;
            }
            // If we're shrinking or not using a custom default,
            // we can just use the default implementation.
            if (initialSize > size || defaultValue === undefined) {
                return list.setSize(size);
            }
            // Our enhancement comes from when we're growing the array, because we
            // allow custom default values.
            return list.withMutations(function (mutable) {
                for (var i = 0; i < size - initialSize; i++) {
                    mutable.push(defaultValue);
                }
            });
        });
    };
    Object.defineProperty(ImmutableState.prototype, "size", {
        get: function () {
            return this._size;
        },
        enumerable: false,
        configurable: true
    });
    ImmutableState.prototype.set = function (key, value) {
        return this.newInstance(this.state.set(key, value));
    };
    ImmutableState.prototype.delete = function (key) {
        return this.newInstance(this.state.delete(key));
    };
    ImmutableState.prototype.remove = function (key) {
        return this.newInstance(this.state.remove(key));
    };
    ImmutableState.prototype.clear = function () {
        return this.newInstance(this.state.clear());
    };
    ImmutableState.prototype.withMutations = function (mutator) {
        return this.newInstance(this.state.withMutations(mutator));
    };
    ImmutableState.prototype.asMutable = function () {
        return this.newInstance(this.state.asMutable());
    };
    ImmutableState.prototype.asImmutable = function () {
        return this.newInstance(this.state.asImmutable());
    };
    ImmutableState.prototype.equals = function (other) {
        return this.state.equals(other);
    };
    ImmutableState.prototype.hashCode = function () {
        return this.state.hashCode();
    };
    ImmutableState.prototype.get = function (key, _notSetValue) {
        return this.state.get(key);
    };
    ImmutableState.prototype.has = function (key) {
        return this.state.has(key);
    };
    ImmutableState.prototype.includes = function (value) {
        return this.state.includes(value);
    };
    ImmutableState.prototype.contains = function (value) {
        return this.state.contains(value);
    };
    ImmutableState.prototype.first = function () {
        return this.state.first();
    };
    ImmutableState.prototype.last = function () {
        return this.state.last();
    };
    ImmutableState.prototype.toJS = function () {
        return this.state.toJS();
    };
    ImmutableState.prototype.toArray = function () {
        return this.state.toArray();
    };
    ImmutableState.prototype.toObject = function () {
        return this.state.toObject();
    };
    ImmutableState.prototype.toMap = function () {
        return this.state.toMap();
    };
    ImmutableState.prototype.toOrderedMap = function () {
        return this.state.toOrderedMap();
    };
    ImmutableState.prototype.toSet = function () {
        return this.state.toSet();
    };
    ImmutableState.prototype.toOrderedSet = function () {
        return this.state.toOrderedSet();
    };
    ImmutableState.prototype.toList = function () {
        return this.state.toList();
    };
    ImmutableState.prototype.toStack = function () {
        return this.state.toStack();
    };
    ImmutableState.prototype.toSeq = function () {
        return this.state.toSeq();
    };
    ImmutableState.prototype.toKeyedSeq = function () {
        return this.state.toKeyedSeq();
    };
    ImmutableState.prototype.toIndexedSeq = function () {
        return this.state.toIndexedSeq();
    };
    ImmutableState.prototype.toSetSeq = function () {
        return this.state.toSetSeq();
    };
    ImmutableState.prototype.keys = function () {
        return this.state.keys();
    };
    ImmutableState.prototype.values = function () {
        return this.state.values();
    };
    ImmutableState.prototype.entries = function () {
        return this.state.entries();
    };
    ImmutableState.prototype.keySeq = function () {
        return this.state.keySeq();
    };
    ImmutableState.prototype.valueSeq = function () {
        return this.state.valueSeq();
    };
    ImmutableState.prototype.entrySeq = function () {
        return this.state.entrySeq();
    };
    ImmutableState.prototype.map = function (mapper, context) {
        return this.state.map(mapper, context);
    };
    ImmutableState.prototype.filter = function (predicate, context) {
        return this.state.filter(predicate, context);
    };
    ImmutableState.prototype.filterNot = function (predicate, context) {
        return this.state.filterNot(predicate, context);
    };
    ImmutableState.prototype.reverse = function () {
        return this.state.reverse();
    };
    ImmutableState.prototype.sort = function (comparator) {
        return this.state.sort(comparator);
    };
    ImmutableState.prototype.sortBy = function (comparatorValueMapper, comparator) {
        return this.state.sortBy(comparatorValueMapper, comparator);
    };
    ImmutableState.prototype.groupBy = function (grouper, context) {
        return this.state.groupBy(grouper, context);
    };
    ImmutableState.prototype.forEach = function (sideEffect, context) {
        return this.state.forEach(sideEffect, context);
    };
    ImmutableState.prototype.slice = function (begin, end) {
        return this.state.slice(begin, end);
    };
    ImmutableState.prototype.rest = function () {
        return this.rest();
    };
    ImmutableState.prototype.butLast = function () {
        return this.state.butLast();
    };
    ImmutableState.prototype.skip = function (amount) {
        return this.state.skip(amount);
    };
    ImmutableState.prototype.skipLast = function (amount) {
        return this.state.skipLast(amount);
    };
    ImmutableState.prototype.skipWhile = function (predicate, context) {
        return this.state.skipWhile(predicate, context);
    };
    ImmutableState.prototype.skipUntil = function (predicate, context) {
        return this.state.skipUntil(predicate, context);
    };
    ImmutableState.prototype.take = function (amount) {
        return this.state.take(amount);
    };
    ImmutableState.prototype.takeLast = function (amount) {
        return this.state.takeLast(amount);
    };
    ImmutableState.prototype.takeWhile = function (predicate, context) {
        return this.state.takeWhile(predicate, context);
    };
    ImmutableState.prototype.takeUntil = function (predicate, context) {
        return this.state.takeUntil(predicate, context);
    };
    ImmutableState.prototype.concat = function () {
        var valuesOrIterables = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            valuesOrIterables[_i] = arguments[_i];
        }
        return this.state.concat(valuesOrIterables);
    };
    ImmutableState.prototype.flatten = function (depth, shallow) {
        if (depth) {
            return this.state.flatten(depth);
        }
        return this.state.flatten(shallow);
    };
    ImmutableState.prototype.flatMap = function (mapper, context) {
        return this.state.flatMap(mapper, context);
    };
    ImmutableState.prototype.reduce = function (reducer, initialReduction, context) {
        return this.state.reduce(reducer, initialReduction, context);
    };
    ImmutableState.prototype.reduceRight = function (reducer, initialReduction, context) {
        return this.state.reduceRight(reducer, initialReduction, context);
    };
    ImmutableState.prototype.every = function (predicate, context) {
        return this.state.every(predicate, context);
    };
    ImmutableState.prototype.some = function (predicate, context) {
        return this.state.some(predicate, context);
    };
    ImmutableState.prototype.join = function (separator) {
        return this.state.join(separator);
    };
    ImmutableState.prototype.isEmpty = function () {
        return this.state.isEmpty();
    };
    ImmutableState.prototype.count = function (predicate, context) {
        return this.state.count(predicate, context);
    };
    ImmutableState.prototype.countBy = function (grouper, context) {
        return this.state.countBy(grouper, context);
    };
    ImmutableState.prototype.find = function (predicate, context, notSetValue) {
        return this.state.find(predicate, context, notSetValue);
    };
    ImmutableState.prototype.findLast = function (predicate, context, notSetValue) {
        return this.state.findLast(predicate, context, notSetValue);
    };
    ImmutableState.prototype.findEntry = function (predicate, context, notSetValue) {
        return this.state.findEntry(predicate, context, notSetValue);
    };
    ImmutableState.prototype.findLastEntry = function (predicate, context, notSetValue) {
        return this.state.findLastEntry(predicate, context, notSetValue);
    };
    ImmutableState.prototype.findKey = function (predicate, context) {
        return this.state.findKey(predicate, context);
    };
    ImmutableState.prototype.findLastKey = function (predicate, context) {
        return this.state.findLastKey(predicate, context);
    };
    ImmutableState.prototype.keyOf = function (searchValue) {
        return this.state.keyOf(searchValue);
    };
    ImmutableState.prototype.lastKeyOf = function (searchValue) {
        return this.state.lastKeyOf(searchValue);
    };
    ImmutableState.prototype.max = function (comparator) {
        return this.state.max(comparator);
    };
    ImmutableState.prototype.maxBy = function (comparatorValueMapper, comparator) {
        return this.state.maxBy(comparatorValueMapper, comparator);
    };
    ImmutableState.prototype.min = function (comparator) {
        return this.state.min(comparator);
    };
    ImmutableState.prototype.minBy = function (comparatorValueMapper, comparator) {
        return this.state.minBy(comparatorValueMapper, comparator);
    };
    ImmutableState.prototype.isSubset = function (iter) {
        return this.state.isSubset(iter);
    };
    ImmutableState.prototype.isSuperset = function (iter) {
        return this.isSuperset(iter);
    };
    ImmutableState.prototype.hasIn = function (searchKeyPath) {
        return this.state.hasIn(searchKeyPath);
    };
    ImmutableState.prototype.getIn = function (searchKeyPath, notSetValue) {
        return this.state.getIn(searchKeyPath, notSetValue);
    };
    ImmutableState.prototype.update = function (updater, key, notSetValue) {
        return this.newInstance(this.state.update(updater, key, notSetValue));
    };
    ImmutableState.prototype.merge = function (iterables) {
        return this.newInstance(this.state.merge(iterables));
    };
    ImmutableState.prototype.mergeWith = function (merger) {
        return this.newInstance(this.state.mergeWith(merger));
    };
    ImmutableState.prototype.mergeDeep = function (iterables) {
        return this.newInstance(this.state.mergeDeep(iterables));
    };
    ImmutableState.prototype.mergeIn = function (keyPath, iterables) {
        return this.newInstance(this.state.mergeIn(keyPath, iterables));
    };
    ImmutableState.prototype.mergeDeepWith = function (merger) {
        return this.newInstance(this.state.mergeDeepWith(merger));
    };
    ImmutableState.prototype.mergeDeepIn = function (keyPath, iterables) {
        return this.newInstance(this.state.mergeDeepIn(keyPath, iterables));
    };
    ImmutableState.prototype.setIn = function (keyPath, value) {
        return this.newInstance(this.state.setIn(keyPath, value));
    };
    ImmutableState.prototype.deleteIn = function (keyPath) {
        return this.newInstance(this.state.deleteIn(keyPath));
    };
    ImmutableState.prototype.removeIn = function (keyPath) {
        return this.newInstance(this.state.removeIn(keyPath));
    };
    ImmutableState.prototype.updateIn = function (keyPath, notSetValue, updater) {
        return this.newInstance(this.state.updateIn(keyPath, notSetValue, updater));
    };
    ImmutableState.prototype.flip = function () {
        return this.state.flip();
    };
    ImmutableState.prototype.mapKeys = function (mapper, context) {
        return this.state.mapKeys(mapper, context);
    };
    ImmutableState.prototype.mapEntries = function (mapper, context) {
        return this.state.mapEntries(mapper, context);
    };
    return ImmutableState;
}());
exports.ImmutableState = ImmutableState;
//# sourceMappingURL=state.js.map